import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    viewAllAfter = "showViewAllAfter",
    viewAllText = "viewAllText",
}

interface OwnProps {
    fields: {
        [fields.viewAllAfter]: number;
        [fields.viewAllText]: string;
    };
}

type Props = OwnProps & WidgetProps & HasCategoryContext;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewAllBtn?: LinkPresentationProps;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        gap: 0,
        css: css`
            background: ${getColor("secondary")};
            border-radius: 6px;
            margin: 0px auto 10px;
        `,
        offsetCss: css`
            max-width: 100%;
            max-height: 300px;
            overflow: auto;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            padding: 8px 12px;
            border-bottom: 1px solid ${getColor("common.border")};
            &:last-of-type {
                border-bottom: none;
            }
        `,
    },
    viewAllBtn: {
        css: css`
            text-decoration: underline;
            font-weight: 500;
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const PLPCategoryDetailLinkList = ({ category, fields }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    const [viewAll, setViewAll] = React.useState(false);

    React.useEffect(() => {
        if (category.path) {
            setViewAll(false);
        }
    }, [category.path]);

    const subCategoryIdsToRender =
        !viewAll && category.subCategoryIds.length > Number(fields.showViewAllAfter)
            ? category.subCategoryIds.slice(0, fields.showViewAllAfter)
            : category.subCategoryIds;
    return (
        <GridContainer {...styles.container}>
            {subCategoryIdsToRender.map(subCategoryId => (
                <GridItem key={subCategoryId.toString()} {...styles.subCategoryNameLinkItem}>
                    <SubCategoryLink subCategoryId={subCategoryId} />
                </GridItem>
            ))}
            {!viewAll && category.subCategoryIds.length > Number(fields.showViewAllAfter) && (
                <GridItem {...styles.subCategoryNameLinkItem}>
                    <Link
                        href=""
                        {...styles.viewAllBtn}
                        onClick={() => setViewAll(true)}
                        data-test-selector="categoryDetailsLinkListLink_viewAll_btn"
                    >
                        {fields.viewAllText}
                    </Link>
                </GridItem>
            )}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string }) => {
    return {
        category: getCategoryState(state, ownProps.subCategoryId).value,
    };
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            data-test-selector={`categoryDetailsLinkListLink_${category.id}`}
        >
            {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(PLPCategoryDetailLinkList),
    definition: {
        displayName: "PLP Category Detail Link List",
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.viewAllAfter,
                editorTemplate: "IntegerField",
                fieldType: "General",
                displayName: "Show View All After",
                defaultValue: 5,
                isRequired: true,
            },
            {
                name: fields.viewAllText,
                editorTemplate: "TextField",
                fieldType: "General",
                displayName: "View All Text",
                defaultValue: "View All",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
